import { AxiosResponse, AxiosError } from 'axios';

import { ResetStateAction } from 'services/auth/types';

import {
    SprayEntityIds,
    SprayApplicationRate
} from 'services/sprayEvents/types';
import { Entity, EntityId } from 'services/entities/types';

import {
    EQUIPMENT_SET_CREATE_REQUESTED,
    EQUIPMENT_SET_CREATE_SUCCEEDED,
    EQUIPMENT_SET_CREATE_FAILED,
    EQUIPMENT_SET_UPDATE_REQUESTED,
    EQUIPMENT_SET_UPDATE_SUCCEEDED,
    EQUIPMENT_SET_UPDATE_FAILED,
    EQUIPMENT_SETS_SET,
    EQUIPMENT_SET_SET,
    EQUIPMENT_SET_DELETE_REQUESTED,
    EQUIPMENT_SET_DELETE_FAILED,
    EQUIPMENT_SET_DELETE_SUCCEEDED,
    NozzleType
} from './constants';

// Calibration Types
export type CalibrationId = number;
export type CalibrationSprayEquipmentSetId = EquipmentSetId;
export type CalibrationRowSpacingMin = number;
export type CalibrationRowSpacingMax = number;
export type CalibrationGear = string;
export type CalibrationSpeed = number;
export type CalibrationFlowRate = number;
export type CalibrationGaugePressure = number;
export type CalibrationApplicationRate = number;
export type CalibrationEngineRpm = number;
export type CalibrationFanSpeed = string;
export type CalibrationSwathWidth = number;
export type CalibrationNozzlesPerSide = number;
export type CalibrationCreatedByUserId = string;
export type CalibrationCreatedAt = string;
export type CalibrationUpdatedAt = string;
export type CalibrationDeletedAt = string;

// Equipment Set Types
export type EquipmentSetId = number;
export type EquipmentSetName = string;
export type EquipmentSetEquipmentType = string;
export type EquipmentSetOrganizationId = string;
export type EquipmentSetInternalId = number;
export type EquipmentSetTankVolume = number;
export type EquipmentSetNozzleType = NozzleType;
export type EquipmentSetCreatedAt = string;
export type EquipmentSetUpdatedAt = string;
export type EquipmentSetDeletedAt = string;
export type EquipmentSetCalibrations = Calibration[];
export type EquipmentSetTractorId = number;
export type EquipmentSetSprayerId = number;
export type EquipmentSetNozzleSetId = number;
export type EquipmentSetNozzleSetEquipmentType = string;
export type EquipmentSetEntities = Entity[] | [];
export type EquipmentSetUuid = string;

export enum CalibrationPressureUnit {
    KPA = 'KPA',
    BAR = 'BAR'
}
export interface CalibrationPayload {
    sprayEquipmentSetId: CalibrationSprayEquipmentSetId;
    rowSpacingMin: CalibrationRowSpacingMin;
    rowSpacingMax: CalibrationRowSpacingMax;
    gear: CalibrationGear;
    speed: CalibrationSpeed;
    flowRate: CalibrationFlowRate;
    gaugePressure: CalibrationGaugePressure;
    pressureUnit: CalibrationPressureUnit;
    applicationRate: CalibrationApplicationRate;
    engineRpm: CalibrationEngineRpm;
    fanSpeed?: CalibrationFanSpeed;
    swathWidth?: CalibrationSwathWidth;
    nozzlesPerSide?: CalibrationNozzlesPerSide;
}

export interface Calibration extends CalibrationPayload {
    id: CalibrationId;
}

export type CalibrationProps = {
    calibrationId: CalibrationId;
};

export interface EquipmentSet {
    id: EquipmentSetId;
    name: EquipmentSetName;
    equipmentType: EquipmentSetEquipmentType;
    organizationId: EquipmentSetOrganizationId;
    internalId: EquipmentSetInternalId;
    tankVolume: EquipmentSetTankVolume;
    nozzleType: EquipmentSetNozzleType;
    calibrations: EquipmentSetCalibrations;
    sprayerId: EquipmentSetSprayerId;
    tractorId: EquipmentSetTractorId;
    nozzleSetId: EquipmentSetNozzleSetId;
    nozzleSetEquipmentType: EquipmentSetNozzleSetEquipmentType;
    entities: EquipmentSetEntities;
    uuid: EquipmentSetUuid;
}

export type PostEquipmentSetPayload = Pick<
    EquipmentSet,
    'sprayerId' | 'tractorId' | 'nozzleSetId' | 'uuid'
> & {
    calibrations?: CalibrationPayload[];
    entity?: {
        id: EntityId;
        seasonId: number;
    };
};

export type PatchEquipmentSetPayload = Pick<EquipmentSet, 'calibrations'>;

export type EquipmentSetsState = Record<EquipmentSetId, EquipmentSet>;

export type EquipmentSetsProps = {
    equipmentSetId?: EquipmentSetId;
    calibrationId?: CalibrationId;
    entityIds: SprayEntityIds;
    applicationRate: SprayApplicationRate;
};

export interface RequestedCreateEquipmentSetAction {
    type: typeof EQUIPMENT_SET_CREATE_REQUESTED;
    payload: PostEquipmentSetPayload;
}

export type RequestedCreateEquipmentSetActionCreator = (
    equipmentSet: PostEquipmentSetPayload
) => RequestedCreateEquipmentSetAction;

export interface SuccceededCreateEquipmentSetAction {
    type: typeof EQUIPMENT_SET_CREATE_SUCCEEDED;
    payload: AxiosResponse;
}

export type SuccceededCreateEquipmentSetActionCreator = (
    response: AxiosResponse
) => SuccceededCreateEquipmentSetAction;

export interface FailedCreateEquipmentSetAction {
    type: typeof EQUIPMENT_SET_CREATE_FAILED;
    payload: AxiosError;
    error: true;
}

export type FailedCreateEquipmentSetActionCreator = (
    error: AxiosError
) => FailedCreateEquipmentSetAction;

export interface RequestDeleteEquipmentSetAction {
    type: typeof EQUIPMENT_SET_DELETE_REQUESTED;
    payload: {
        equipmentSetId: EquipmentSetId;
    };
}

export type RequestedDeleteEquipmentSetActionCreator = (
    equipmentSetId: EquipmentSetId
) => RequestDeleteEquipmentSetAction;

export interface FailedDeleteEquipmentSetAction {
    type: typeof EQUIPMENT_SET_DELETE_FAILED;
    payload: AxiosError;
    error: true;
}

export type FailedDeleteEquipmentSetActionCreator = (
    error: AxiosError
) => FailedDeleteEquipmentSetAction;

export interface SuccceededDeleteEquipmentSetAction {
    type: typeof EQUIPMENT_SET_DELETE_SUCCEEDED;
    payload: {
        equipmentSetId: EquipmentSetId;
    };
}

export type SuccceededDeleteEquipmentSetActionCreator = (
    equipmentSetId: EquipmentSetId
) => SuccceededDeleteEquipmentSetAction;

export interface RequestedUpdateEquipmentSetAction {
    type: typeof EQUIPMENT_SET_UPDATE_REQUESTED;
    payload: {
        equipmentSetId: EquipmentSetId;
        equipmentSet: PatchEquipmentSetPayload;
    };
}

export type RequestedUpdateEquipmentSetActionCreator = (
    equipmentSetId: EquipmentSetId,
    equipmentSet: PatchEquipmentSetPayload
) => RequestedUpdateEquipmentSetAction;

export interface SuccceededUpdateEquipmentSetAction {
    type: typeof EQUIPMENT_SET_UPDATE_SUCCEEDED;
    payload: AxiosResponse;
}

export type SuccceededUpdateEquipmentSetActionCreator = (
    response: AxiosResponse
) => SuccceededUpdateEquipmentSetAction;

export interface FailedUpdateEquipmentSetAction {
    type: typeof EQUIPMENT_SET_UPDATE_FAILED;
    payload: AxiosError;
    error: true;
}

export type FailedUpdateEquipmentSetActionCreator = (
    error: AxiosError
) => FailedUpdateEquipmentSetAction;

export interface SetEquipmentSetsAction {
    type: typeof EQUIPMENT_SETS_SET;
    payload: Record<EquipmentSetId, EquipmentSet>;
}

export type SetEquipmentSetsActionCreator = (
    equipmentSetsKeyedById: Record<EquipmentSetId, EquipmentSet>
) => SetEquipmentSetsAction;

export interface SetEquipmentSetAction {
    type: typeof EQUIPMENT_SET_SET;
    payload: {
        equipmentSetId: EquipmentSetId;
        equipmentSet: EquipmentSet;
    };
}

export type SetEquipmentSetActionCreator = (
    equipmentSetId: EquipmentSetId,
    equipmentSet: EquipmentSet
) => SetEquipmentSetAction;

export type EquipmentSetsActions =
    | SetEquipmentSetsAction
    | SetEquipmentSetAction
    | SuccceededDeleteEquipmentSetAction
    | ResetStateAction;
